import { useState, useEffect } from 'react'
import {
  Box,
  Alert,
  AlertTitle,
  IconButton,
  Collapse,
  Chip,
  Tooltip,
  alpha,
  useTheme,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import NewReleasesIcon from '@mui/icons-material/NewReleases'

export type AnnouncementType = 'banner' | 'badge' | 'tooltip' | 'inline'

export interface FeatureAnnouncement {
  id: string
  type: AnnouncementType
  title?: string
  message: string
  dismissible?: boolean
  persistent?: boolean // If true, shows until dismissed (not just once)
  priority?: 'low' | 'medium' | 'high'
  actionLabel?: string
  onAction?: () => void
  onDismiss?: () => void
  icon?: React.ReactNode
  color?: 'info' | 'success' | 'warning' | 'error'
}

interface FeatureAnnouncementProps {
  announcement: FeatureAnnouncement
  onDismiss?: (id: string) => void
}

const STORAGE_KEY_PREFIX = 'feature_announcement_'

// Check if announcement has been dismissed
const isDismissed = (id: string): boolean => {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(`${STORAGE_KEY_PREFIX}${id}`) === 'dismissed'
}

// Mark announcement as dismissed
const markDismissed = (id: string): void => {
  if (typeof window === 'undefined') return
  localStorage.setItem(`${STORAGE_KEY_PREFIX}${id}`, 'dismissed')
}

// Clear dismissed status (for testing or reset)
export const clearDismissedStatus = (id: string): void => {
  if (typeof window === 'undefined') return
  localStorage.removeItem(`${STORAGE_KEY_PREFIX}${id}`)
}

export function FeatureAnnouncementBanner({
  announcement,
  onDismiss,
}: FeatureAnnouncementProps) {
  const [open, setOpen] = useState(true)
  const theme = useTheme()

  useEffect(() => {
    // Check if already dismissed (unless persistent)
    if (!announcement.persistent && isDismissed(announcement.id)) {
      setOpen(false)
    }
  }, [announcement.id, announcement.persistent])

  const handleDismiss = () => {
    setOpen(false)
    if (!announcement.persistent) {
      markDismissed(announcement.id)
    }
    onDismiss?.(announcement.id)
    announcement.onDismiss?.()
  }

  if (!open) return null

  return (
    <Collapse in={open}>
      <Alert
        severity={announcement.color || 'info'}
        icon={announcement.icon || <InfoIcon />}
        action={
          announcement.dismissible !== false ? (
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          ) : null
        }
        sx={{
          mb: 2,
          borderRadius: 2,
          boxShadow: `0 4px 20px ${alpha(theme.palette[announcement.color || 'info'].main, 0.3)}, 0 2px 8px rgba(0,0,0,0.15)`,
          border: `2px solid ${alpha(theme.palette[announcement.color || 'info'].main, 0.4)}`,
          '& .MuiAlert-message': {
            width: '100%',
          },
          '& .MuiAlert-icon': {
            fontSize: '1.5rem',
            opacity: 1,
          },
          '& .MuiAlertTitle-root': {
            fontWeight: 700,
            fontSize: '1rem',
          },
          '& .MuiTypography-body2': {
            fontWeight: 500,
            fontSize: '0.95rem',
          },
        }}
      >
        {announcement.title && (
          <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
            {announcement.title}
          </AlertTitle>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ flex: 1 }}>
            {announcement.message}
          </Typography>
          {announcement.actionLabel && announcement.onAction && (
            <Chip
              label={announcement.actionLabel}
              onClick={announcement.onAction}
              size="small"
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            />
          )}
        </Box>
      </Alert>
    </Collapse>
  )
}

export function FeatureAnnouncementBadge({
  announcement,
  onDismiss,
}: FeatureAnnouncementProps) {
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    if (!announcement.persistent && isDismissed(announcement.id)) {
      setVisible(false)
    }
  }, [announcement.id, announcement.persistent])

  const handleClick = () => {
    if (announcement.onAction) {
      announcement.onAction()
    }
    if (!announcement.persistent) {
      markDismissed(announcement.id)
      setVisible(false)
      onDismiss?.(announcement.id)
      announcement.onDismiss?.()
    }
  }

  if (!visible) return null

  return (
    <Tooltip title={announcement.message} arrow>
      <Chip
        icon={<NewReleasesIcon />}
        label={announcement.title || 'New'}
        size="small"
        color={announcement.color || 'primary'}
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
          fontWeight: 600,
          '&:hover': {
            opacity: 0.8,
          },
        }}
      />
    </Tooltip>
  )
}

export function FeatureAnnouncementInline({
  announcement,
  onDismiss,
}: FeatureAnnouncementProps) {
  const [open, setOpen] = useState(true)
  const theme = useTheme()

  useEffect(() => {
    if (!announcement.persistent && isDismissed(announcement.id)) {
      setOpen(false)
    }
  }, [announcement.id, announcement.persistent])

  const handleDismiss = () => {
    setOpen(false)
    if (!announcement.persistent) {
      markDismissed(announcement.id)
    }
    onDismiss?.(announcement.id)
    announcement.onDismiss?.()
  }

  if (!open) return null

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1.5,
        borderRadius: 1,
        backgroundColor: alpha(
          theme.palette[announcement.color || 'info'].main,
          0.1
        ),
        border: `1px solid ${alpha(
          theme.palette[announcement.color || 'info'].main,
          0.3
        )}`,
      }}
    >
      {announcement.icon || <InfoIcon sx={{ color: 'text.secondary' }} />}
      <Typography variant="body2" sx={{ flex: 1 }}>
        {announcement.message}
      </Typography>
      {announcement.dismissible !== false && (
        <IconButton size="small" onClick={handleDismiss}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  )
}

// Main component that renders based on type
export default function FeatureAnnouncement({
  announcement,
  onDismiss,
}: FeatureAnnouncementProps) {
  switch (announcement.type) {
    case 'banner':
      return (
        <FeatureAnnouncementBanner
          announcement={announcement}
          onDismiss={onDismiss}
        />
      )
    case 'badge':
      return (
        <FeatureAnnouncementBadge
          announcement={announcement}
          onDismiss={onDismiss}
        />
      )
    case 'inline':
      return (
        <FeatureAnnouncementInline
          announcement={announcement}
          onDismiss={onDismiss}
        />
      )
    case 'tooltip':
      // Tooltip is handled differently - typically wraps an element
      return null
    default:
      return null
  }
}

